import axios from "../../axios/AxiosInstance";
import { useEffect, useRef, useState } from "react";
import { Popover } from "antd";
import { ChevronDown, Mail, X } from "lucide-react";
import {
  invoiceRelances,
  formatRelanceDate,
} from "../../utils/invoiceRelances";

function RelanceCards({ relances }) {
  return (
    <ol
      aria-label="Relances envoyées"
      className="max-h-[min(60vh,440px)] divide-y divide-slate-100 overflow-y-auto pr-1"
    >
      {relances.map((relance) => (
        <li
          key={relance.id}
          className="px-2 py-3 first:pt-1 last:pb-1"
        >
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <span className={`text-xs font-semibold ${relance.message === "Premier envoi" ? "text-slate-600" : "text-blue-700"}`}>
              {relance.message}
            </span>
            <time
              dateTime={relance.date || undefined}
              title={relance.date ? formatRelanceDate(relance.date) : undefined}
              className="shrink-0 text-xs tabular-nums text-slate-400"
            >
              {relance.date
                ? formatRelanceDate(relance.date).split(" ")[0]
                : "Date inconnue"}
            </time>
          </div>
          <p className="mb-0 mt-1 flex items-start gap-2 text-xs leading-5 text-slate-600">
            <Mail
              size={13}
              aria-hidden="true"
              className="mt-1 shrink-0 text-slate-400"
            />
            <span className="break-all">
              {relance.contactEmail || "Destinataire non renseigné"}
            </span>
          </p>
        </li>
      ))}
    </ol>
  );
}

export default function InvoiceRelanceHistory({ bill, expanded = false }) {
  const [history, setHistory] = useState(bill);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const trigger = useRef(null);
  useEffect(() => {
    setHistory(bill);
  }, [bill]);
  useEffect(() => {
    setOpen(false);
    setError("");
    setLoaded(false);
  }, [bill._id]);
  useEffect(() => {
    if (!expanded && !open) return;
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`facture/${bill._id}/comments`);
        if (active) {
          setHistory((prev) => ({ ...prev, ...data }));
          setError("");
          setLoaded(true);
        }
      } catch {
        if (active)
          setError("Impossible de charger l’historique des relances.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    window.addEventListener("focus", load);
    return () => {
      active = false;
      window.removeEventListener("focus", load);
    };
  }, [expanded, open, bill._id]);
  useEffect(() => {
    if (!open) return;
    const escape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener("keydown", escape, true);
    return () => document.removeEventListener("keydown", escape, true);
  }, [open]);
  const relances = invoiceRelances(history._id === bill._id ? history : bill);
  const count = relances.length;
  const known = loaded || Array.isArray(bill.relance);
  const cards = (
    <>
      {loading && (
        <p role="status" className="mb-3 text-xs text-slate-500">
          Actualisation de l’historique…
        </p>
      )}
      {error && (
        <p role="alert" className="mb-3 text-sm text-red-700">
          {error}
        </p>
      )}
      {count > 0 ? (
        <RelanceCards relances={relances} />
      ) : (
        !loading &&
        !error && (
          <p className="py-4 text-sm text-slate-500">
            Aucune relance enregistrée.
          </p>
        )
      )}
    </>
  );
  if (expanded)
    return (
      <section aria-label="Historique des relances" className="text-left">
        {cards}
      </section>
    );
  return (
    <span onClick={(event) => event.stopPropagation()}>
      <Popover
        trigger="click"
        placement="top"
        open={open}
        onOpenChange={setOpen}
        content={
          <section
            role="dialog"
            aria-label="Historique des relances"
            onClick={(event) => event.stopPropagation()}
            className="w-[min(420px,calc(100vw-64px))] text-left"
          >
            <div className="mb-4 flex items-center justify-between gap-3 border-b border-slate-200 pb-3">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Mail size={18} className="text-blue-600" />
                Historique des relances ({count})
              </h3>
              <button
                type="button"
                aria-label="Fermer l’historique des relances"
                onClick={() => {
                  setOpen(false);
                  trigger.current?.focus();
                }}
                className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
              >
                <X size={16} />
              </button>
            </div>
            {cards}
          </section>
        }
      >
        <button
          ref={trigger}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          className="inline-flex min-w-[90px] items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
        >
          <span>
            {known ? (
              <>
                <span className="block text-sm leading-4">{count}</span>
                <span className="block">
                  {count === 1 ? "relance" : "relances"}
                </span>
              </>
            ) : (
              "Historique"
            )}
          </span>
          <ChevronDown size={14} className={open ? "rotate-180" : ""} />
        </button>
      </Popover>
    </span>
  );
}
